import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useCanvasStore, useUIStore } from '@/stores';
import { NODE_TYPE_META } from '@/components/canvas/nodeTypes';
import { Lightbulb, Sparkles, UserRound, X } from 'lucide-react';
import type { NodeMetadata } from '@/services/canvas-mappers';
import { findingTypeLabel } from '@/lib/market-categories';
import { useCanvasMutations } from '@/hooks/use-canvas-mutations';
import { formatRespondentFrequency } from '@/lib/utils';
import { SectionLabel } from '@/components/ui/section-label';

export function NodeDetailsModal({ projectId }: { projectId: string }) {
  const activeModal = useUIStore((s) => s.activeModal);
  const setActiveModal = useUIStore((s) => s.setActiveModal);
  const setViewMode = useUIStore((s) => s.setViewMode);
  const canEdit = useUIStore((s) => s.canEdit);
  const nodes = useCanvasStore((s) => s.nodes);
  const { updateNodeData, deleteNode } = useCanvasMutations(projectId);

  const [label, setLabel] = useState('');
  const [content, setContent] = useState('');

  const isOpen = activeModal?.startsWith('edit-node:') ?? false;
  const nodeId = activeModal?.replace('edit-node:', '') ?? '';
  const node = nodes.find((n) => n.id === nodeId);
  const meta = node?.type ? NODE_TYPE_META[node.type as keyof typeof NODE_TYPE_META] : null;

  useEffect(() => {
    if (node) {
      setLabel((node.data?.label as string) || '');
      setContent((node.data?.content as string) || '');
    }
  }, [node]);

  if (!isOpen || !node || !meta) return null;

  const nodeMetadata = (node.data?.metadata as NodeMetadata | undefined) ?? undefined;
  const typeLabel = node.type === 'market_signal' ? findingTypeLabel(nodeMetadata) : meta.label;
  const isFromDialog = nodeMetadata?.source === 'dialog';
  const isFromIdeaCapture = nodeMetadata?.source === 'idea-capture';
  const isSynthesised = nodeMetadata?.source === 'synthesis';
  const respondent = nodeMetadata?.respondentEmail || nodeMetadata?.respondentName;

  // Provenance for a synthesised node: which raw findings it groups and how many
  // distinct respondents raised the need ("N of M"; M absent on older nodes).
  const memberItemIds = nodeMetadata?.memberItemIds ?? [];
  const frequency = formatRespondentFrequency(nodeMetadata?.respondentCount, nodeMetadata?.respondentTotal);
  // id → title lookup built once (members resolve against it) rather than a
  // linear find per member.
  const memberLabelById = isSynthesised ? new Map(nodes.map((n) => [n.id, (n.data?.label as string) || ''])) : null;

  // Only fields actually present render — keeps the segment line clean for
  // partial context (just role, or role+team, etc.) and skips the line
  // entirely for anonymous / legacy unsegmented nodes.
  const segment = nodeMetadata?.segment;
  const segmentParts: string[] = [];
  if (segment?.role) segmentParts.push(segment.role);
  if (segment?.team) segmentParts.push(segment.team);
  if (segment?.company) segmentParts.push(segment.company);

  // Idea-capture nodes carry the PM's hypothesised target
  // audience in `customerSegment` — distinct shape from per-respondent
  // `segment` above. Modal is read-only for idea nodes; canonical edit
  // lives in the idea workspace's form to avoid two sources of truth.
  const ideaAspiration = isFromIdeaCapture ? nodeMetadata?.aspiration : undefined;
  const ideaSegment = isFromIdeaCapture ? nodeMetadata?.customerSegment : undefined;
  const ideaSegmentParts: string[] = [];
  if (ideaSegment?.role) ideaSegmentParts.push(ideaSegment.role);
  if (ideaSegment?.industry) ideaSegmentParts.push(ideaSegment.industry);
  if (ideaSegment?.description) ideaSegmentParts.push(ideaSegment.description);

  const handleSave = () => {
    updateNodeData(nodeId, { label, content });
    setActiveModal(null);
  };

  const handleDelete = () => {
    deleteNode(nodeId);
    setActiveModal(null);
  };

  const handleEditInIdeaView = () => {
    setActiveModal(null);
    setViewMode('idea');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background border rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-semibold">{isFromIdeaCapture ? meta.label : `Edit ${typeLabel}`}</h2>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setActiveModal(null)}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {isFromIdeaCapture ? (
          <>
            <div className="p-4 space-y-4">
              <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-muted/50 text-xs text-muted-foreground">
                <Lightbulb className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden />
                <div>
                  <div className="font-medium text-foreground">From idea capture</div>
                  <div>Edit in the Idea capture workspace to change these values.</div>
                </div>
              </div>
              <ReadOnlyRow label="Product idea" value={label} multiline />
              <ReadOnlyRow label="Aspiration" value={ideaAspiration} />
              <ReadOnlyRow label="Customer segment" value={ideaSegmentParts.join(' · ') || undefined} />
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => setActiveModal(null)}>
                Close
              </Button>
              <Button onClick={handleEditInIdeaView}>Edit idea</Button>
            </div>
          </>
        ) : (
          <>
            <div className="p-4 space-y-4">
              {isFromDialog && (
                <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-muted/50 text-xs text-muted-foreground">
                  <UserRound className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden />
                  <div>
                    <div className="font-medium text-foreground">From dialog</div>
                    <div>
                      Submitted by {respondent || 'a dialog respondent'}
                      {nodeMetadata?.edited ? ' · edited before submission' : ''}
                    </div>
                    {segmentParts.length > 0 && (
                      <div className="mt-0.5">Segment: {segmentParts.join(' · ')}</div>
                    )}
                  </div>
                </div>
              )}
              {isSynthesised && (
                <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-muted/50 text-xs text-muted-foreground">
                  <Sparkles className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden />
                  <div className="min-w-0">
                    <div className="font-medium text-foreground">Synthesised theme</div>
                    {frequency && <div>{frequency}</div>}
                    {memberItemIds.length > 0 && (
                      <>
                        <div className="mt-1">
                          Synthesised from {memberItemIds.length} finding{memberItemIds.length === 1 ? '' : 's'}:
                        </div>
                        <ul className="mt-0.5 list-disc pl-4 space-y-0.5">
                          {memberItemIds.map((id) => (
                            <li key={id} className="truncate">
                              {memberLabelById?.get(id) || '(removed)'}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              )}
              <div>
                <label className="text-sm font-medium">Title</label>
                <textarea
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  readOnly={!canEdit}
                  className="w-full mt-1 px-3 py-2 border rounded-md bg-background focus:outline-hidden focus:ring-2 focus:ring-ring resize-none read-only:opacity-70"
                  rows={2}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  readOnly={!canEdit}
                  className="w-full mt-1 px-3 py-2 border rounded-md bg-background focus:outline-hidden focus:ring-2 focus:ring-ring resize-none read-only:opacity-70"
                  rows={4}
                />
              </div>
            </div>

            {/* Viewers get a read-only modal — no Save/Delete, just Close. */}
            {canEdit ? (
              <div className="flex items-center justify-between p-4 border-t">
                <Button variant="destructive" size="sm" onClick={handleDelete}>
                  Delete
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setActiveModal(null)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave}>Save</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-end p-4 border-t">
                <Button variant="outline" onClick={() => setActiveModal(null)}>
                  Close
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ReadOnlyRow({ label, value, multiline }: { label: string; value?: string; multiline?: boolean }) {
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      <div className={`mt-0.5 text-sm ${multiline ? 'whitespace-pre-wrap' : ''}`}>
        {value || <span className="text-muted-foreground italic">Not set</span>}
      </div>
    </div>
  );
}
